// src/context/AuthContext.jsx
// Autenticación simplificada: solo JWT de Supabase Auth
// El rol viene de user_metadata.role (seteado en signUp)
// No hay lógica de RLS ni búsqueda compleja de perfiles
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import db from '../services/dbProxy';
import { coachService } from '../services/supabaseService';

const AuthContext = createContext(null);

// Tabla de perfil según rol
const ROLE_TABLE = {
  club: 'clubs',
  coach: 'coaches',
  judge: 'judges',
  organizer: 'organizers',
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Error persistente de autenticación: sobrevive a signOut automático
  // para poder mostrar el motivo del rechazo en el formulario de login
  // después de que Supabase haga el bounce.
  const [authError, setAuthError] = useState(null);

  // Timestamp del último signIn explícito del usuario. Se usa para detectar
  // eventos SIGNED_OUT espurios que dispara el cliente interno de Supabase
  // (p.ej. por sync de storage multi-tab post-logout de otro rol).
  const lastExplicitSignInRef = useRef(0);
  // Guardamos los tokens del último signIn exitoso para poder restaurar
  // la sesión si gotrue la elimina espuriamente.
  const lastSignInTokensRef = useRef(null);

  // El rol viene SIEMPRE del JWT (user_metadata), nunca de la BD
  const role = user?.user_metadata?.role || null;

  // Carga el perfil de la tabla correspondiente usando el proxy /api/v1
  const loadProfile = async (authUser) => {
    if (!authUser) { setProfile(null); return; }
    const userRole = authUser.user_metadata?.role;
    if (userRole === 'admin') { setProfile({ name: 'Administrador' }); return; }
    const table = ROLE_TABLE[userRole];
    if (!table) { setProfile(null); return; }

    // Polling sin timeout fijo: reintenta cada 3s hasta tener respuesta
    // No bloquea la app — si falla definitivamente, profile queda null
    let attempts = 0;
    const maxAttempts = 5;

    // Validar estado del perfil (activo/aprobado). Si no cumple, forzar signOut.
    const validateAndSet = async (profileRow) => {
      if (!profileRow) { setProfile(null); return; }

      // Chequeo universal: is_active debe ser distinto de false
      if (profileRow.is_active === false) {
        const msg = 'Tu cuenta está inactiva. Contacta al administrador.';
        console.warn('[AUTH] Perfil inactivo, cerrando sesión:', profileRow.id);
        setAuthError(msg);
        lastExplicitSignInRef.current = 0;
        lastSignInTokensRef.current = null;
        try { await supabase.auth.signOut(); } catch (_) {}
        return;
      }

      // Chequeo específico para clubes
      if (table === 'clubs') {
        if (profileRow.approval_status === 'pending_approval') {
          const msg = 'Tu cuenta está pendiente de aprobación. Espera que el administrador la habilite.';
          console.warn('[AUTH] Club pendiente, cerrando sesión:', profileRow.id);
          setAuthError(msg);
          lastExplicitSignInRef.current = 0;
          lastSignInTokensRef.current = null;
          try { await supabase.auth.signOut(); } catch (_) {}
          return;
        }
        if (profileRow.approval_status === 'rejected') {
          const msg = 'Tu solicitud fue rechazada. Contacta al administrador para más información.';
          console.warn('[AUTH] Club rechazado, cerrando sesión:', profileRow.id);
          setAuthError(msg);
          lastExplicitSignInRef.current = 0;
          lastSignInTokensRef.current = null;
          try { await supabase.auth.signOut(); } catch (_) {}
          return;
        }
      }

      setProfile(profileRow);
    };

    const tryLoad = async () => {
      try {
        console.log('[AuthContext] Loading from table:', table, 'with auth_user_id:', authUser.id);
        // Direct query - usar la tabla correcta según el rol
        const { data, error } = await db(table)
          .select('*')
          .eq('auth_user_id', authUser.id)
          .limit(1)
          .execute();

        console.log('[AuthContext] Query result:', { table, data, error });
        if (data && data.length > 0) {
          await validateAndSet(data[0]);
          return;
        }

        // Fallback by email
        const fallback = await db(table)
          .select('*')
          .eq('email', (authUser.email || '').toLowerCase())
          .limit(1)
          .execute();

        if (fallback.data && fallback.data.length > 0) {
          await validateAndSet(fallback.data[0]);
          return;
        }

        setProfile(null);
      } catch (err) {
        setProfile(null);
      }
    };

    tryLoad();
  };

  // Escuchar cambios de sesión (única fuente de verdad)
  useEffect(() => {
    let mounted = true;

    // Ping silencioso para despertar la función serverless (cold start)
    // Solo funciona en Vercel, no en desarrollo local
    // fetch('/api/ping').catch(() => {});

    // 1. Cargar sesión inicial una sola vez
    const initSession = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(initialSession);
        setUser(initialSession?.user || null);
        if (initialSession?.user) {
          await loadProfile(initialSession.user);
          // Sesión restaurada desde storage → arrancar refresh automático
          try { await supabase.auth.startAutoRefresh(); } catch (_) {}
        }
      } catch (err) {
        console.error('[AUTH] Error inicializando sesión:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initSession();

    // 2. Escuchar cambios posteriores (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('[AUTH] onAuthStateChange EVENT:', event, 'hasSession:', !!newSession, 'user.id:', newSession?.user?.id);
        if (!mounted) return;
        // Ignorar INITIAL_SESSION — ya lo maneja initSession()
        if (event === 'INITIAL_SESSION') return;

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          setSession(newSession);
          setUser(newSession?.user || null);
          if (newSession?.user) {
            await loadProfile(newSession.user);
            // Arrancar refresh automático recién ahora, con la sesión estable.
            // Si lo dejamos activado desde el constructor, su tick corre en
            // paralelo con signInWithPassword y dispara SIGNED_OUT espurios.
            if (event === 'SIGNED_IN') {
              try { await supabase.auth.startAutoRefresh(); } catch (_) {}
            }
          }
        } else if (event === 'SIGNED_OUT') {
          // DEFENSIVO: ignorar SIGNED_OUT espurios que dispara el cliente
          // interno de Supabase (bug conocido con multi-tab/locks post-logout,
          // y también inmediato post-signIn con autoRefresh + StrictMode).
          const sinceSignIn = Date.now() - lastExplicitSignInRef.current;
          if (sinceSignIn < 10000) {
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (currentSession?.user) {
              console.warn('[AUTH] SIGNED_OUT espurio ignorado (sesión aún válida, hace', sinceSignIn, 'ms del signIn)');
              return;
            }
            console.warn('[AUTH] SIGNED_OUT legítimo pese a signIn reciente — sesión realmente perdida');
          }
          lastSignInTokensRef.current = null;
          setSession(null);
          setUser(null);
          setProfile(null);
          cleanupLegacy();
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (error) {
        if (error.message?.includes('Invalid login credentials')) return { error: 'Correo o contraseña incorrectos' };
        if (error.message?.includes('Email not confirmed')) return { error: 'Confirma tu correo antes de iniciar sesión' };
        return { error: error.message };
      }

      // Marcar timestamp para detectar SIGNED_OUT espurios inmediatos
      lastExplicitSignInRef.current = Date.now();
      // Capturar tokens para poder restaurar la sesión si gotrue la borra
      if (data?.session?.access_token && data?.session?.refresh_token) {
        lastSignInTokensRef.current = {
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        };
      }

      console.log('[AUTH] signIn OK — user_metadata:', data.user?.user_metadata, 'user.id:', data.user?.id);
      let userRole = data.user?.user_metadata?.role;
      console.log('[AUTH] userRole extraído:', userRole);

      // RECUPERACIÓN DE ROL: si falta user_metadata.role (cuenta legacy o
      // creada fuera del flujo signUp), buscar en las tablas de perfil
      // por auth_user_id y persistir el rol en el metadata.
      if (!userRole && data.user) {
        console.warn('[AUTH] user_metadata.role faltante — intentando recuperar desde DB...');
        const tableToRole = { clubs: 'club', coaches: 'coach', judges: 'judge', organizers: 'organizer' };
        for (const t of Object.keys(tableToRole)) {
          try {
            const { data: prof } = await db(t)
              .select('id')
              .eq('auth_user_id', data.user.id)
              .limit(1)
              .execute();
            if (prof && prof.length > 0) {
              userRole = tableToRole[t];
              console.log(`[AUTH] Rol recuperado: ${userRole} (tabla ${t}). Persistiendo en user_metadata...`);
              try {
                await supabase.auth.updateUser({ data: { role: userRole } });
              } catch (updErr) {
                console.warn('[AUTH] No se pudo persistir el rol:', updErr?.message);
              }
              break;
            }
          } catch (e) {
            console.warn(`[AUTH] Error buscando en ${t}:`, e?.message);
          }
        }
        if (!userRole) {
          console.error('[AUTH] No se encontró perfil asociado para auth_user_id:', data.user.id);
        }
      }

      // NOTA: el chequeo de is_active/approval_status se hace en loadProfile
      // (después de que la sesión está estable). Hacerlo acá causaba un race
      // con el manejo interno de sesión de Supabase post-logout de otro rol.

      // Login OK → limpiar cualquier error previo
      setAuthError(null);

      return { data, error: null };
    } catch (err) {
      console.error('[AUTH] signIn exception:', err);
      return { error: 'Error al iniciar sesión. Intenta nuevamente.' };
    }
  };

  // Registro — crear usuario auth desde un endpoint local en vez de llamar directamente a Supabase desde el cliente
  const signUp = async (email, password, role, profileData = null) => {
    try {
      const metadata = {
        role,
        ...(profileData?.name ? { name: profileData.name } : {}),
      };

      const response = await fetch('/api/register-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          metadata,
          role,
          profileData,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        if (result.error?.includes('already registered')) return { error: 'Este correo ya está registrado.' };
        return { error: result.error || 'Error al registrar. Intenta nuevamente.' };
      }
      return { data: { user: result.user, profile: result.profile }, error: null };
    } catch (err) {
      console.error('[AUTH] Error en signUp:', err);
      return { error: 'Error al registrar. Intenta nuevamente.' };
    }
  };

  // Cerrar sesión
  const signOut = async () => {
    // signOut explícito → invalidar tokens capturados para que el handler de
    // SIGNED_OUT no intente restaurar la sesión
    lastExplicitSignInRef.current = 0;
    lastSignInTokensRef.current = null;
    try { await supabase.auth.stopAutoRefresh(); } catch (_) {}
    try { await supabase.auth.signOut(); } catch (_) {}
    setSession(null);
    setUser(null);
    setProfile(null);
    cleanupLegacy();
  };

  // Recargar perfil (después de updates)
  const refreshProfile = async () => {
    if (user) await loadProfile(user);
  };


  // En AuthContext, cuando cargas el perfil del coach
const loadCoachProfile = async (authUserId) => {
  const { data } = await coachService.getByAuthId(authUserId);
  if (data) {
    setProfile({
      ...data,
      photo_url: data.photo_url, // Asegurar que este campo esté presente
      // otros campos...
    });
  }
};

  // Reset password
  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error?.message || null };
  };

  // Update password (for logged in user)
  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error: error?.message || null };
  };

  // Limpiar claves legacy de localStorage
  const cleanupLegacy = () => {
    ['judgeId','judgeName','judgeEmail','isJudge',
     'coachId','coachName','coachEmail','isCoach',
     'clubId','clubName','clubEmail','isClub',
     'organizerId','organizerName','organizerEmail','isOrganizer','isAdmin']
      .forEach(k => localStorage.removeItem(k));
  };

  const clearAuthError = () => setAuthError(null);

  const value = {
    session,
    user,
    profile,
    role,
    loading,
    authError,
    clearAuthError,
    isAuthenticated: !!session,
    // Helpers de rol
    isAdmin: role === 'admin',
    isClub: role === 'club',
    isCoach: role === 'coach',
    isJudge: role === 'judge',
    isOrganizer: role === 'organizer',
    // Acciones
    signIn,
    signUp,
    signOut,
    refreshProfile,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
};

export default AuthContext;
